import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { Button } from '@dtx-company/design-system/src/components/Button/Button'
import { Close as CloseIcon, Email as EmailIcon, Print as PrintIcon } from '@mui/icons-material'
import { DownloadTemplateButton } from './DownloadTemplateButton'
import { FC, MutableRefObject } from 'react'
import { IconButton } from '@dtx-company/design-system/src'
import { ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { Stack } from '@dtx-company/design-system/src/components/Stack/Stack'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useDownloadOrPrintTemplate, useEmailTemplate } from './DownloadTemplateModal.hooks'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import dynamic from 'next/dynamic'
import printStoreEvents from '@dtx-company/inter-app/src/event-tracking/events/printStore'
import type { DrawerProps } from '@dtx-company/design-system/src/components/Drawer/Drawer'
import type { PopoverProps } from '@mui/material/Popover'

const Drawer = dynamic<DrawerProps>(
  () => import('@dtx-company/design-system/src/components/Drawer/Drawer').then(mod => mod.Drawer),
  { ssr: false }
)
const Popover = dynamic<PopoverProps>(
  () => import('@mui/material/Popover').then(mod => mod.default),
  { ssr: false }
)

interface DownloadTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  anchorRef: MutableRefObject<HTMLButtonElement | null>
}

export const DownloadTemplateModal: FC<DownloadTemplateModalProps> = ({
  isOpen,
  onClose,
  anchorRef
}) => {
  const { isMobile } = useScreenSizeBreakpoints()
  const ModalComponent = isMobile ? Drawer : Popover
  const { batchId } = useCodeSelector(state => state.printStore)
  const {
    onDownloadClick,
    onPrintClick,
    printIsFetching,
    downloadIsFetching,
    setExportFormat,
    exportFormat
  } = useDownloadOrPrintTemplate()
  const { isFetching: emailIsFetching } = useEmailTemplate()
  const { setOpen: setEmailTemplateModalOpen } = useModal(ModalTypes.EMAIL_PLACEMENT_TEMPLATE_MODAL)
  const { setClose: setDownloadTemplateModalClose } = useModal(ModalTypes.TEMPLATES_DOWNLOAD_MODAL)

  return (
    <ModalComponent
      open={isOpen}
      onClose={onClose}
      anchor="bottom"
      anchorEl={anchorRef.current}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{
        sx: {
          py: { xs: 4, md: 9.25 },
          px: { xs: 5, md: 11.25 },
          borderTopLeftRadius: theme => ({ xs: theme.spacing(2), md: theme.spacing(3) }),
          borderTopRightRadius: theme => ({ xs: theme.spacing(2), md: theme.spacing(3) }),
          borderBottomLeftRadius: theme => ({ xs: 0, md: theme.spacing(3) }),
          borderBottomRightRadius: theme => ({ xs: 0, md: theme.spacing(3) }),
          maxWidth: { xs: 'unset', md: 'min-content' }
        }
      }}
      aria-labelledby="download-modal-title"
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'flex-end'
          }}
        >
          <IconButton icon={CloseIcon} onClick={onClose} variant="text" aria-label="Close" />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: 2.5
          }}
        >
          <DownloadTemplateButton
            onDownloadClick={onDownloadClick}
            loading={downloadIsFetching}
            disabled={!batchId}
            setExportFormat={setExportFormat}
            selectedExportFormat={exportFormat}
          />
          <Button
            color="secondary"
            fullWidth
            startIcon={EmailIcon}
            onClick={() => {
              setDownloadTemplateModalClose()
              printStoreEvents.PrintTemplates_TemplateEditor_DownloadModal_clicked_email()
              setEmailTemplateModalOpen()
            }}
            loading={emailIsFetching}
            disabled={!batchId}
          >
            Email
          </Button>
          <Button
            color="secondary"
            fullWidth
            startIcon={PrintIcon}
            onClick={onPrintClick}
            loading={printIsFetching}
            disabled={!batchId}
          >
            Print
          </Button>
        </Box>
      </Stack>
    </ModalComponent>
  )
}
